import type { TFunction } from 'i18next'

import { formatPhone } from '#/shared/lib/format'
import type {
  ConversationItemSchema,
  TimelineCall,
  TimelineChat,
  TimelineSms,
} from '#/shared/api/generated/communication/models'

import type {
  ChatPhoto,
  ConversationDirection,
  MessageStatus,
  TimelineItem,
} from '../model/conversation'
import {
  DECLINED_CALL_STATUSES,
  MISSED_CALL_STATUSES,
  NO_ANSWER_STATUSES,
  callDurationLabel,
  callRoute,
} from './format-call'
import { dateGroupLabel, timeLabel } from './relative-date'

/** API direction string → the view model's compact direction. */
function toDirection(direction: string): ConversationDirection {
  return direction === 'outbound' ? 'out' : 'in'
}

/** Derive a thumbnail filename from a media URL ("…/altima-front.jpg?x=1"). */
function fileNameFromUrl(url: string): string {
  const path = url.split('?')[0]
  return decodeURIComponent(path.slice(path.lastIndexOf('/') + 1)) || url
}

function mediaToPhotos(
  urls: string[] | undefined,
  caption: string,
): ChatPhoto[] | undefined {
  if (!urls || urls.length === 0) return undefined
  return urls.map((url) => ({ url, name: fileNameFromUrl(url), caption }))
}

/** Outgoing delivery status → bubble state; inbound bubbles carry no check. */
function messageStatus(sms: TimelineSms): MessageStatus | undefined {
  if (sms.direction !== 'outbound') return undefined
  switch (sms.status) {
    case 'queued':
    case 'sending':
      return 'sending'
    case 'sent':
      return 'sent'
    case 'delivered':
      return 'delivered'
    case 'failed':
      return 'failed'
    default:
      return undefined
  }
}

/** Display name of the agent who sent a message, e.g. "Jane Doe"; "" when the
 *  owning user is unknown (system-sent) or unnamed. */
export function userName(user: ConversationItemSchema['user']): string {
  if (!user) return ''
  return [user.first_name, user.last_name].filter(Boolean).join(' ')
}

/** Sender → recipient line shown under a tapped bubble. Outgoing collapses to
 *  just the line it was sent from (the recipient is the open thread). When it
 *  went out on the shared mainline, the sending agent's name leads the line,
 *  e.g. "Jane Doe · (415) 404-9036" — so a shared number shows who sent it. */
function smsRoute(item: ConversationItemSchema, sms: TimelineSms): string {
  const from = formatPhone(sms.from_phone)
  if (sms.direction === 'outbound') {
    const sentFromMainline = item.mainline?.phone_number === sms.from_phone
    const name = sentFromMainline ? userName(item.user) : ''
    return name ? `${name} · ${from}` : from
  }
  const to = sms.to_phones?.[0]
  return to ? `${from} → ${formatPhone(to)}` : from
}

/** Caption under the full-screen photo viewer (DEV-700): the sending agent
 *  when known (outbound), then the sending line — falling back to the thread's
 *  mainline number when the item carries no from number — then the time:
 *  "John Doe - (415) 404-9036 - 2:46 PM". */
function photoCaption(item: ConversationItemSchema, sms: TimelineSms): string {
  const name = sms.direction === 'outbound' ? userName(item.user) : ''
  const phone =
    formatPhone(sms.from_phone) || formatPhone(item.mainline?.phone_number)
  return [name, phone, timeLabel(item.occurred_at)].filter(Boolean).join(' - ')
}

function smsToMessage(
  item: ConversationItemSchema,
  sms: TimelineSms,
): TimelineItem {
  const errorDetail = sms.error?.trim()
  const automationNote = item.automation?.note.trim()
  return {
    kind: 'message',
    id: item.id,
    at: item.occurred_at,
    direction: toDirection(sms.direction),
    text: sms.body ?? '',
    meta: timeLabel(item.occurred_at),
    status: messageStatus(sms),
    errorDetail: errorDetail === '' ? undefined : errorDetail,
    photos: mediaToPhotos(sms.media_urls, photoCaption(item, sms)),
    route: smsRoute(item, sms),
    mainline: item.mainline?.phone_number === sms.from_phone,
    automationNote: automationNote === '' ? undefined : automationNote,
  }
}

/** In-app portal chat message → bubble. It carries no phone route, no media and
 *  no delivery state — the portal only reports the text and when it was sent —
 *  so the entry is deliberately thin and `chat` flags it for the green bubble. */
function chatToMessage(
  item: ConversationItemSchema,
  chat: TimelineChat,
): TimelineItem {
  return {
    kind: 'message',
    id: item.id,
    at: item.occurred_at,
    direction: toDirection(chat.direction),
    text: chat.body ?? '',
    meta: timeLabel(item.occurred_at),
    chat: true,
  }
}

function callTitle(call: TimelineCall, inbound: boolean, t: TFunction): string {
  if (MISSED_CALL_STATUSES.has(call.status))
    return t('phonePanel.call.missedCall')
  if (NO_ANSWER_STATUSES.has(call.status)) return t('phonePanel.call.noAnswer')
  return inbound
    ? t('phonePanel.call.inboundCall')
    : t('phonePanel.call.outboundCall')
}

function callToEntry(
  item: ConversationItemSchema,
  call: TimelineCall,
  t: TFunction,
  agentName: string,
): TimelineItem {
  const inbound = call.direction !== 'outbound'
  return {
    kind: 'call',
    id: item.id,
    at: item.occurred_at,
    direction: toDirection(call.direction),
    title: callTitle(call, inbound, t),
    agent: userName(item.user) || agentName || undefined,
    subtitle: [callDurationLabel(call.duration_seconds), callRoute(call)]
      .filter(Boolean)
      .join(' · '),
    time: timeLabel(item.occurred_at),
    ai: call.ai_handled,
    missed: MISSED_CALL_STATUSES.has(call.status),
    noAnswer: NO_ANSWER_STATUSES.has(call.status),
    declined: DECLINED_CALL_STATUSES.has(call.status),
    callId: call.id,
    hasRecording: call.has_recording,
    durationSeconds: call.duration_seconds ?? undefined,
    summary: call.summary ? { summary: call.summary } : undefined,
  }
}

/** SMS bubble, or null when the message carries neither text nor media. */
function smsEntry(
  item: ConversationItemSchema,
  sms: TimelineSms,
): TimelineItem | null {
  const hasText = (sms.body ?? '').trim().length > 0
  const hasMedia = (sms.media_urls?.length ?? 0) > 0
  return hasText || hasMedia ? smsToMessage(item, sms) : null
}

/** Chat bubble, or null when the message carries no text. */
function chatEntry(
  item: ConversationItemSchema,
  chat: TimelineChat,
): TimelineItem | null {
  return (chat.body ?? '').trim() ? chatToMessage(item, chat) : null
}

/** One timeline item → message/call entry, or null when there's nothing to show
 *  (empty SMS with no body and no media, an empty chat, or an empty payload). */
function toEntry(
  item: ConversationItemSchema,
  t: TFunction,
  agentName: string,
): TimelineItem | null {
  if (item.sms) return smsEntry(item, item.sms)
  if (item.chat) return chatEntry(item, item.chat)
  if (item.call) return callToEntry(item, item.call, t, agentName)
  return null
}

/** ISO timestamp of an entry; date separators have none. */
function entryTime(entry: TimelineItem): string {
  return entry.kind === 'date' ? '' : entry.at
}

/** Map raw conversation items to message/call entries, oldest → newest.
 *  `agentName` is the thread's owning agent (personal-line threads) — used on
 *  call entries when the item itself carries no user. */
export function itemsToEntries(
  items: ReadonlyArray<ConversationItemSchema>,
  t: TFunction,
  agentName = '',
): TimelineItem[] {
  return items
    .flatMap((item) => {
      const entry = toEntry(item, t, agentName)
      return entry ? [entry] : []
    })
    .sort((a, b) => entryTime(a).localeCompare(entryTime(b)))
}

/** Insert a date separator before each run of entries on a new day. */
export function withDateSeparators(
  entries: ReadonlyArray<TimelineItem>,
  t: TFunction,
): TimelineItem[] {
  const timeline: TimelineItem[] = []
  let lastDay = ''
  for (const entry of entries) {
    const at = entryTime(entry)
    const day = dateGroupLabel(at, t)
    if (day && day !== lastDay) {
      timeline.push({ kind: 'date', id: `date-${entry.id}`, label: day, at })
      lastDay = day
    }
    timeline.push(entry)
  }
  return timeline
}
